import React, { useState } from 'react';
import InvalidateOrganisationCache, { InvalidateOrganisationCacheResult } from '../../services/adapters/invalidateOrganisationCache';

interface Props {
  displayName: string;
}

type InvalidateState =
  | { stage: 'idle' }
  | { stage: 'confirming' }
  | { stage: 'submitting' }
  | { stage: 'done'; result: InvalidateOrganisationCacheResult }
  | { stage: 'error'; message: string };

export default function InvalidateOrganisationCachePanel({ displayName }: Readonly<Props>) {
  const [state, setState] = useState<InvalidateState>({ stage: 'idle' });

  const handleConfirm = async () => {
    setState({ stage: 'submitting' });
    try {
      const result = await new InvalidateOrganisationCache().adaptResponse({ organisationDisplayName: displayName });
      setState({ stage: 'done', result });
    } catch (e) {
      setState({ stage: 'error', message: e instanceof Error ? e.message : 'Cache invalidation failed.' });
    }
  };

  return (
    <div className="border border-pipgrey-light rounded-md p-4 flex flex-col gap-3">
      <p className="text-sm font-semibold text-piptext-default">Invalidate query cache</p>
      <p className="text-xs text-piptext-secondary">
        Removes every cached dashboard query result for this organisation&apos;s LIFT clients.
        The next request for each query recomputes it from the database. Safe to run at any
        time; use after a data correction that did not go through the migration workflow.
      </p>

      {state.stage === 'error' && (
        <p className="text-xs text-pipstatus-error">{state.message}</p>
      )}

      {state.stage === 'done' && (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-pipstatus-success">
            {`Invalidated ${state.result.invalidatedKeyCount} cached ${state.result.invalidatedKeyCount === 1 ? 'query' : 'queries'}`}
            {state.result.liftClientNames.length > 0 && ` for ${state.result.liftClientNames.join(', ')}`}
            .
          </p>
          {state.result.warnings.length > 0 && (
            <ul className="text-xs text-pipstatus-error list-disc list-inside">
              {state.result.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {(state.stage === 'idle' || state.stage === 'done' || state.stage === 'error') && (
        <button
          type="button"
          className="self-start button-base button-default"
          onClick={() => setState({ stage: 'confirming' })}
        >
          Invalidate cache…
        </button>
      )}

      {state.stage === 'confirming' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-piptext-default font-medium">
            {'Invalidate all cached queries for '}
            <span className="font-bold">{`${displayName}?`}</span>
            {' '}
            Dashboards will be slower until the cache re-fills.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="button-base button-default"
              onClick={handleConfirm}
            >
              Confirm invalidation
            </button>
            <button
              type="button"
              className="button-base button-default"
              onClick={() => setState({ stage: 'idle' })}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {state.stage === 'submitting' && (
        <p className="text-xs text-piptext-secondary">Invalidating…</p>
      )}
    </div>
  );
}
